/**
 * Seating that carries on round a bend between two continuation pieces
 * (user, 2026-11-04: "check if the seating can simply continue around the
 * bend to the next section" — then "yes").
 *
 * A continuation turned twice (the 45-45 shape) is two pieces with a bend
 * between them. Where the pieces' backs — the seating side — are on the
 * OUTSIDE of that bend, their knee strips fan apart: nothing of one stands in
 * the other's way, and the seating can run on round the bend as one stretch.
 * Seated piece by piece instead, each was cut along the other's line and lost
 * its stools (Robinson/Mckinzie's 45" + 36" and 54" + 30" shapes: none).
 *
 * So the pieces of such a run are seated together or not at all:
 *   - neither slab is cut at the bend between them (`through` on each one's
 *     seating, the end it carries on from);
 *   - the wedge between their two overhangs on the outside of the bend is
 *     filled, on the earlier piece's slab, so the counter reads as one;
 *   - the stools are spread along the whole stretch at the usual spacing and
 *     the two-stool minimum is counted over the whole stretch, not per piece.
 * The wedge and the through ends are held in each piece's own frame on its
 * seating record, so the run engine's re-solve, which cannot see the other
 * piece, builds the same counter (`seatedPeninsulaTop`).
 *
 * Only pieces of ONE continuation, consecutive, at a bend whose outside the
 * seating is on. Where the seating is on the inside, the strips collide and
 * each piece keeps its own cut (`seatedSlabCut.js`).
 *
 * Pure. Metres.
 */
const { getWallAxes } = require('./wallMath');

const JOIN_TOL = 0.05;
const SIDE_EPS = 0.05;

const dist = (p, q) => Math.hypot(p.x - q.x, p.z - q.z);

/** A point in an arm's own frame: `a` along its line, `n` toward its cabinets. */
function toLocal(ax, p) {
  return {
    a: (p.x - ax.start.x) * ax.wallDir.x + (p.z - ax.start.z) * ax.wallDir.z,
    n:
      (p.x - ax.start.x) * ax.wallNormal.x +
      (p.z - ax.start.z) * ax.wallNormal.z,
  };
}

/** Which end of `ax` (0 or its length) lies at the other arm's nearer end. */
function endAt(ax, other) {
  const d = (p) => Math.min(dist(p, other.start), dist(p, other.end));
  return d(ax.start) <= d(ax.end)
    ? { a: 0, point: ax.start, gap: d(ax.start) }
    : { a: ax.wallLen, point: ax.end, gap: d(ax.end) };
}

/**
 * The bend between two consecutive pieces, or null when they do not meet.
 * `outside`: the seating (each piece's back, −normal) is on the outside of the
 * bend — the next piece leaves the bend toward this one's CABINET side.
 */
function bendBetween(armA, armB) {
  const ax = getWallAxes(armA);
  const bx = getWallAxes(armB);
  if (!ax || !bx) return null;
  const atA = endAt(ax, bx);
  const atB = endAt(bx, ax);
  if (atA.gap > JOIN_TOL) return null;
  const far = atB.a === 0 ? bx.end : bx.start;
  const away = { x: far.x - atB.point.x, z: far.z - atB.point.z };
  const l = Math.hypot(away.x, away.z) || 1;
  const dn = (away.x * ax.wallNormal.x + away.z * ax.wallNormal.z) / l;
  return { aEnd: atA.a, bEnd: atB.a, outside: dn > SIDE_EPS };
}

const continuationOf = (arm) => arm?.peninsulaSource?.continuation || null;

/**
 * Whether a piece runs straight on from the wall it continues from. Its back
 * then lies on that wall's own line, and seated, its overhang would stand the
 * whole knee depth past it — what the cut at the wall exists to prevent — so
 * it never joins a stretch.
 */
function inLineWithItsWall(arm, walls) {
  const c = continuationOf(arm);
  if (!c || c.index !== 0) return false;
  const wall = (walls || []).find((w) => w?.id === c.wallId);
  const ax = getWallAxes(arm);
  const wx = wall && getWallAxes(wall);
  if (!ax || !wx) return false;
  const cross = ax.wallDir.x * wx.wallDir.z - ax.wallDir.z * wx.wallDir.x;
  return Math.abs(cross) < SIDE_EPS;
}

/**
 * The stretches seating may run along: runs of two or more consecutive pieces
 * of one continuation, every bend between them with the seating on its
 * outside, none straight on from its wall. Each stretch lists its arms in
 * continuation order. `walls` are the room's (the wall a run continues from).
 */
function stretchesOf(arms, walls) {
  const byRun = new Map();
  for (const arm of arms || []) {
    const c = continuationOf(arm);
    if (!c) continue;
    if (!byRun.has(c.wallId)) byRun.set(c.wallId, []);
    byRun.get(c.wallId).push(arm);
  }
  const out = [];
  for (const all of byRun.values()) {
    const run = all
      .filter((arm) => !inLineWithItsWall(arm, walls))
      .sort((x, y) => continuationOf(x).index - continuationOf(y).index);
    if (!run.length) continue;
    let current = [run[0]];
    for (let i = 1; i < run.length; i++) {
      const prev = run[i - 1];
      const consecutive =
        continuationOf(run[i]).index === continuationOf(prev).index + 1;
      const bend = consecutive ? bendBetween(prev, run[i]) : null;
      if (bend?.outside) {
        current.push(run[i]);
      } else {
        if (current.length > 1) out.push(current);
        current = [run[i]];
      }
    }
    if (current.length > 1) out.push(current);
  }
  return out;
}

/**
 * Each arm's `through` ends within a stretch — where its seating carries on
 * round a bend — keyed by arm id: `{ a, wedge? }`. The EARLIER piece of each
 * bend carries the wedge: the neighbour's outer corner and its line's end, in
 * this arm's frame, which with this arm's own outer corner and end close the
 * gap between the two overhangs. `reach` is the overhang's depth from the
 * line (back panel + knee).
 */
function stretchThroughs(stretch, reach) {
  const through = new Map(stretch.map((arm) => [arm.id, []]));
  for (let i = 1; i < stretch.length; i++) {
    const a = stretch[i - 1];
    const b = stretch[i];
    const bend = bendBetween(a, b);
    if (!bend) continue;
    const ax = getWallAxes(a);
    const bx = getWallAxes(b);
    const bPoint = bend.bEnd === 0 ? bx.start : bx.end;
    const bOuter = {
      x: bPoint.x - bx.wallNormal.x * reach,
      z: bPoint.z - bx.wallNormal.z * reach,
    };
    through.get(a.id).push({
      a: bend.aEnd,
      wedge: [toLocal(ax, bOuter), toLocal(ax, bPoint)],
    });
    through.get(b.id).push({ a: bend.bEnd });
  }
  return through;
}

/**
 * Where the stools of a stretch stand: `spans` are each arm's seatable span,
 * in stretch order, each running to the bend it shares with the next. The
 * stools are spread evenly along the spans' combined length, as one edge's
 * stools are (`buildBarstoolObjects`), and each lands on the arm whose span
 * holds it. Fewer than `minSeats` — none.
 *
 * @param {{arm: Object, start: number, end: number}[]} spans
 * @returns {{arm: Object, at: number}[]} each stool's arm and its param on it
 */
function stretchStools(spans, minSeats) {
  // Required here, not at the top: `barstoolSeating` reaches the run engine
  // (`cabinet-runs/freestanding`), whose fill requires `seatedPeninsulaTop`,
  // which requires this module — loaded at the top, the fill would take an
  // unfinished `seatedPeninsulaTop` and find no `seatRunArms` in it.
  const { seatCountForEdge } = require('./barstoolSeating');
  // Each span's direction along the stretch: from the end away from the next
  // piece toward the bend (or, for the last, from the bend outward).
  const legs = spans.map((s, i) => {
    const ax = getWallAxes(s.arm);
    const next = spans[i + 1]?.arm;
    const prev = spans[i - 1]?.arm;
    let forward;
    if (next) forward = endAt(ax, getWallAxes(next)).a > 0;
    else if (prev) forward = endAt(ax, getWallAxes(prev)).a === 0;
    else forward = true;
    return { ...s, forward, length: s.end - s.start };
  });
  const total = legs.reduce((sum, l) => sum + l.length, 0);
  const seats = seatCountForEdge(total);
  if (seats < minSeats) return [];
  const out = [];
  for (let k = 0; k < seats; k++) {
    let s = (total * (k + 0.5)) / seats;
    for (const leg of legs) {
      if (s <= leg.length + 1e-9) {
        out.push({
          arm: leg.arm,
          at: leg.forward ? leg.start + s : leg.end - s,
        });
        break;
      }
      s -= leg.length;
    }
  }
  return out;
}

const fromLocal = (ax, { a, n }) => ({
  x: ax.start.x + a * ax.wallDir.x + n * ax.wallNormal.x,
  z: ax.start.z + a * ax.wallDir.z + n * ax.wallNormal.z,
});

/**
 * The piece of counter filling the wedge between two seated pieces' knee
 * overhangs on the outside of the bend their seating carries round: this
 * arm's line end and outer corner at the bend, then the neighbour's outer
 * corner and line end (`through.wedge`). Its own countertop — one seam at the
 * bend, as a stone top is seamed there — wearing `slab`'s look and owned by
 * the same cabinets, so the run that re-solves the arm replaces it. Tagged
 * `bendWedge` so another arm's settling leaves it alone. Null where the
 * through end carries no wedge (the later piece of the bend).
 */
function bendWedgeTop(slab, arm, through, reach) {
  if (!through?.wedge?.length || !slab) return null;
  const ax = getWallAxes(arm);
  if (!ax) return null;
  const pts = [
    fromLocal(ax, { a: through.a, n: 0 }),
    fromLocal(ax, { a: through.a, n: -reach }),
    ...through.wedge.map((p) => fromLocal(ax, p)),
  ];
  const cx = pts.reduce((sum, p) => sum + p.x, 0) / pts.length;
  const cz = pts.reduce((sum, p) => sum + p.z, 0) / pts.length;
  const local = pts.map((p) => ({
    a: (p.x - cx) * ax.wallDir.x + (p.z - cz) * ax.wallDir.z,
    n: (p.x - cx) * ax.wallNormal.x + (p.z - cz) * ax.wallNormal.z,
  }));
  const span = (k) =>
    Math.max(...local.map((p) => p[k])) - Math.min(...local.map((p) => p[k]));
  return {
    ...slab,
    id: `${slab.id}_bend_${Math.round(through.a * 1000)}`,
    label: 'Peninsula Seating Round the Bend',
    category: { ...slab.category, bendWedge: true },
    center: { x: cx, y: slab.center.y, z: cz },
    dimensions: { ...slab.dimensions, x: span('a'), z: span('n') },
    polygonCorners: pts,
    parentWallIds: [arm.id],
  };
}

module.exports = {
  bendWedgeTop,
  bendBetween,
  stretchesOf,
  stretchThroughs,
  stretchStools,
};
